import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Save } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Select } from "../../components/Select/Select";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { useToast } from "../../components/Toast/Toast";

import { ApiError, describeError } from "../../lib/errors";
import { getProject } from "../projects/api";
import type { ProfileConfig } from "../projects/config/types";
import { getPlayer, updatePlayer } from "./api";
import { PlayerMetaEditor } from "./PlayerMetaEditor";
import { PlayerStatsEditor } from "./PlayerStatsEditor";
import type { Player, UpdatePlayerInput } from "./types";
import styles from "./PlayerDetailPage.module.css";

// === Formatting helpers ============================================== //

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Feb 14, 2026" — used for the Joined field of the identity card. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return DATE_FMT.format(d);
}

/**
 * Compact relative time — "just now", "2 hours ago", "1 day ago", or an
 * absolute date past a week. Used for the "Last updated" identity field.
 */
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return plural(min, "minute");
  const hr = Math.floor(min / 60);
  if (hr < 24) return plural(hr, "hour");
  const day = Math.floor(hr / 24);
  if (day < 7) return plural(day, "day");
  return DATE_FMT.format(d);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

// === Component ====================================================== //

export function PlayerDetailPage() {
  const { id = "", playerId = "" } = useParams<{
    id: string;
    playerId: string;
  }>();
  const qc = useQueryClient();
  const { push } = useToast();

  // Bumped after every successful save so PlayerDetailForm remounts clean,
  // re-reading its field state from the freshly-saved server record.
  const [saveGen, setSaveGen] = useState(0);

  // The project — shared cache key with PlayersListPage / TeamPage. Read
  // config.profile for the stats schema + allowed-avatars whitelist.
  const projectQuery = useQuery({
    queryKey: ["project", id],
    queryFn: () => getProject(id).then((r) => r.data),
    enabled: !!id,
  });

  // The player record being edited.
  const playerQuery = useQuery({
    queryKey: ["player", id, playerId],
    queryFn: () => getPlayer(id, playerId),
    enabled: !!id && !!playerId,
  });

  const project = projectQuery.data;
  const player = playerQuery.data;

  // games.config is typed `unknown` — narrow to the profile sub-object.
  const profile = (project?.config as { profile?: ProfileConfig } | undefined)
    ?.profile;
  const statsSchema = profile?.stats_schema ?? [];
  const metaSchema = profile?.meta_schema ?? [];
  const allowedAvatars = profile?.allowed_avatars ?? [];

  const header = (
    <PageHeader
      breadcrumb={[
        { label: project?.name ?? "Project" },
        { label: "Players", to: `/projects/${id}/players` },
        { label: player?.display_name ?? "Player" },
      ]}
      title={player?.display_name ?? "Player"}
      actions={
        <Link
          to={`/projects/${id}/players`}
          className={styles.backBtn}
        >
          <ChevronLeft size={13} aria-hidden />
          Back to players
        </Link>
      }
    />
  );

  // === Not-found state =============================================== //
  if (
    playerQuery.isError &&
    playerQuery.error instanceof ApiError &&
    playerQuery.error.status === 404
  ) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Player not found">
                This player doesn&apos;t exist, or it belongs to another
                project.
              </Alert>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Generic load error =========================================== //
  if (projectQuery.isError || playerQuery.isError) {
    const err =
      playerQuery.error instanceof ApiError
        ? playerQuery.error
        : projectQuery.error instanceof ApiError
          ? projectQuery.error
          : null;
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <div className={styles.errorCard}>
              <Alert variant="danger" title="Couldn't load player">
                {err ? describeError(err.code) : describeError("internal")}
              </Alert>
              <div className={styles.errorActions}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void projectQuery.refetch();
                    void playerQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // === Loading skeleton ============================================= //
  // Both queries must resolve before the editor mounts — PlayerStatsEditor
  // reads `schema` only at first mount, so we never render it with an empty
  // schema and swap the real one in later.
  if (projectQuery.isLoading || playerQuery.isLoading || !project || !player) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.content}>
            <LoadingSkeleton />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <PlayerDetailForm
        key={`${player.updated_at}:${saveGen}`}
        projectId={id}
        playerId={playerId}
        player={player}
        statsSchema={statsSchema}
        metaSchema={metaSchema}
        allowedAvatars={allowedAvatars}
        onSaved={(updated) => {
          // Re-sync the form from the server response: write it into the
          // shared cache so `player` reflects the saved record, then bump
          // `saveGen` to remount PlayerDetailForm clean (its useState
          // initializers re-read from the response; the stats editor
          // remounts with it). `invalidateQueries` then refetches in the
          // background to pick up anything else the server changed.
          qc.setQueryData<Player>(["player", id, playerId], updated);
          void qc.invalidateQueries({ queryKey: ["player", id, playerId] });
          // Refresh the project's activity feed so the new "player.updated"
          // event surfaces on the overview page.
          void qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
          setSaveGen((g) => g + 1);
          push({ variant: "success", message: "Player updated" });
        }}
      />
    </>
  );
}

// === Editable form =================================================== //
// Remounted (via the parent's `saveGen` key) after a successful save so its
// useState initializers re-read the freshly-saved record. Discard resets in
// place; the stats editor — uncontrolled after mount — is remounted by its own
// `statsKey` on discard.

interface PlayerDetailFormProps {
  projectId: string;
  playerId: string;
  player: Player;
  statsSchema: ProfileConfig["stats_schema"];
  metaSchema: ProfileConfig["meta_schema"];
  allowedAvatars: string[];
  /** Called with the saved record after a successful PATCH. */
  onSaved: (updated: Player) => void;
}

function PlayerDetailForm({
  projectId,
  playerId,
  player,
  statsSchema,
  metaSchema,
  allowedAvatars,
  onSaved,
}: PlayerDetailFormProps) {
  // A server that predates players.meta (or any partial record) must not
  // hand the uncontrolled editors an undefined object.
  const playerMeta = player.meta ?? {};

  // --- Profile field state ------------------------------------------- //
  const [displayName, setDisplayName] = useState(player.display_name);
  const [avatar, setAvatar] = useState(player.avatar ?? "");
  const [score, setScore] = useState(String(player.score));

  // --- Stats editor state -------------------------------------------- //
  // PlayerStatsEditor is uncontrolled after mount. We mirror its latest value
  // here, track whether it has been touched, and remount it (via statsKey) on
  // discard so it resets back to the player's stats.
  const [stats, setStats] = useState<Record<string, unknown>>(player.stats);
  const [statsDirty, setStatsDirty] = useState(false);
  const [statsValid, setStatsValid] = useState(true);
  const [statsKey, setStatsKey] = useState(0);

  // --- Meta editor state --------------------------------------------- //
  // Same uncontrolled contract as the stats editor above.
  const [meta, setMeta] = useState<Record<string, unknown>>(playerMeta);
  const [metaDirty, setMetaDirty] = useState(false);
  const [metaValid, setMetaValid] = useState(true);
  const [metaKey, setMetaKey] = useState(0);

  // --- Per-field validation ------------------------------------------ //
  const trimmedName = displayName.trim();
  const nameError =
    trimmedName.length < 1 || trimmedName.length > 80
      ? "Display name must be between 1 and 80 characters."
      : undefined;

  // --- Dirty tracking ------------------------------------------------ //
  const nameDirty = displayName !== player.display_name;
  const avatarDirty = avatar !== (player.avatar ?? "");
  const scoreDirty = score !== String(player.score);

  const dirty =
    nameDirty || avatarDirty || scoreDirty || statsDirty || metaDirty;

  const changeCount =
    (nameDirty ? 1 : 0) +
    (avatarDirty ? 1 : 0) +
    (scoreDirty ? 1 : 0) +
    (statsDirty ? 1 : 0) +
    (metaDirty ? 1 : 0);

  // Score is parsed as an int64 admin override. An empty / non-integer value
  // blocks the save the same way a stat type error does.
  const scoreError = useMemo(() => {
    const t = score.trim();
    if (t === "" || !/^[+-]?\d+$/.test(t) || !Number.isSafeInteger(Number(t))) {
      return "Must be a whole number.";
    }
    return undefined;
  }, [score]);

  const errorCount =
    (nameError ? 1 : 0) +
    (scoreError ? 1 : 0) +
    (statsDirty && !statsValid ? 1 : 0) +
    (metaDirty && !metaValid ? 1 : 0);
  const hasError = errorCount > 0;

  // === Save ========================================================== //
  const mutation = useMutation({
    mutationFn: (body: UpdatePlayerInput) =>
      updatePlayer(projectId, playerId, body),
  });

  // 422 field-level errors surfaced from the server response.
  const serverError =
    mutation.error instanceof ApiError ? mutation.error : null;
  const serverAvatarError =
    serverError?.status === 422 && serverError.code === "avatar_not_allowed"
      ? describeError("avatar_not_allowed")
      : undefined;
  const serverStatsError =
    serverError?.status === 422 && serverError.code === "invalid_stats"
      ? describeError("invalid_stats")
      : undefined;
  const serverMetaError =
    serverError?.status === 422 && serverError.code === "invalid_meta"
      ? describeError("invalid_meta")
      : undefined;
  const serverGenericError =
    serverError && !serverAvatarError && !serverStatsError && !serverMetaError
      ? describeError(serverError.code)
      : undefined;

  function doSave() {
    if (hasError || !dirty) return;
    // Only the dirty field groups are sent. `stats` is omitted entirely when
    // the editor is clean so the server doesn't re-validate untouched legacy
    // keys against the schema.
    const body: UpdatePlayerInput = {};
    if (nameDirty) body.display_name = trimmedName;
    if (avatarDirty) body.avatar = avatar;
    if (scoreDirty) body.score = Number(score.trim());
    if (statsDirty) body.stats = stats;
    if (metaDirty) body.meta = meta;
    mutation.mutate(body, {
      onSuccess: (updated) => {
        onSaved(updated);
      },
    });
  }

  function doDiscard() {
    setDisplayName(player.display_name);
    setAvatar(player.avatar ?? "");
    setScore(String(player.score));
    setStats(player.stats);
    setStatsDirty(false);
    setStatsValid(true);
    setMeta(playerMeta);
    setMetaDirty(false);
    setMetaValid(true);
    // Remount both editors so they re-seed from the player's record.
    setStatsKey((k) => k + 1);
    setMetaKey((k) => k + 1);
    mutation.reset();
  }

  const useAvatarSelect = allowedAvatars.length > 0;

  return (
    <>
      <div className={styles.page}>
        <div className={styles.content}>
          {serverGenericError && (
            <div className={styles.errorAlert}>
              <Alert variant="danger" title="Couldn't save changes">
                {serverGenericError}
              </Alert>
            </div>
          )}

          {/* ───────── Identity (read-only) ───────── */}
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <div className={styles.identity}>
                <Avatar name={player.display_name} size={52} />
                <div>
                  <div className={styles.identityName}>
                    {player.display_name}
                  </div>
                  <div className={styles.identitySub}>
                    External ID ·{" "}
                    <span className={styles.mono}>{player.external_id}</span>
                  </div>
                  <MetaSummary schema={metaSchema} meta={player.meta ?? {}} />
                </div>
              </div>
              <div className={styles.kvGrid}>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Player UUID</div>
                  <div className={`${styles.kvVal} ${styles.mono}`}>
                    {player.id}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Joined</div>
                  <div className={styles.kvVal}>
                    {formatDate(player.created_at)}
                  </div>
                </div>
                <div className={styles.kv}>
                  <div className={styles.kvKey}>Last updated</div>
                  <div className={styles.kvVal}>
                    {formatRelative(player.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Profile ───────── */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Profile</h3>
              <span className={styles.cardSub}>
                display name · avatar · score
              </span>
            </div>
            <div className={styles.cardBody}>
              {/* Display name */}
              <div className={`${styles.inputGroup} ${styles.displayNameGroup}`}>
                <label htmlFor="player-display-name">
                  Display name
                  {nameDirty && (
                    <span
                      className={styles.modDot}
                      title="modified"
                      aria-hidden
                    />
                  )}
                </label>
                <input
                  id="player-display-name"
                  className={`${styles.input} ${
                    nameError ? styles.invalid : ""
                  } ${!nameError && nameDirty ? styles.modified : ""}`}
                  value={displayName}
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? "player-name-err" : undefined}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                {nameError && (
                  <div className={styles.fieldError} id="player-name-err">
                    {nameError}
                  </div>
                )}
              </div>

              <div className={styles.formGrid2}>
                {/* Avatar */}
                <div className={styles.inputGroup}>
                  <label htmlFor="player-avatar">
                    Avatar
                    {avatarDirty && (
                      <span
                        className={styles.modDot}
                        title="modified"
                        aria-hidden
                      />
                    )}
                  </label>
                  {useAvatarSelect ? (
                    <Select
                      id="player-avatar"
                      value={avatar}
                      onChange={setAvatar}
                      options={allowedAvatars.map((a) => ({
                        value: a,
                        label: a,
                      }))}
                      placeholder="Select an avatar…"
                      monospaceValue
                    />
                  ) : (
                    <input
                      id="player-avatar"
                      className={`${styles.input} ${styles.mono} ${
                        !avatarDirty ? "" : styles.modified
                      }`}
                      type="text"
                      value={avatar}
                      onChange={(e) => setAvatar(e.target.value)}
                    />
                  )}
                  {serverAvatarError ? (
                    <div className={styles.fieldError}>{serverAvatarError}</div>
                  ) : useAvatarSelect ? (
                    <div className={styles.helper}>
                      From the project&apos;s allowed-avatars whitelist.
                    </div>
                  ) : (
                    <div className={styles.helper}>
                      Whitelist <code className={styles.code}>allowed_avatars</code>{" "}
                      is empty → free-text input, any string accepted.
                    </div>
                  )}
                </div>

                {/* Score */}
                <div className={styles.inputGroup}>
                  <label htmlFor="player-score">
                    Score
                    {scoreDirty && (
                      <span
                        className={styles.modDot}
                        title="modified"
                        aria-hidden
                      />
                    )}
                  </label>
                  <input
                    id="player-score"
                    className={`${styles.input} ${styles.mono} ${
                      scoreError ? styles.invalid : ""
                    } ${!scoreError && scoreDirty ? styles.modified : ""}`}
                    type="text"
                    value={score}
                    aria-invalid={scoreError ? true : undefined}
                    aria-describedby={
                      scoreError ? "player-score-err" : undefined
                    }
                    onChange={(e) => setScore(e.target.value)}
                  />
                  {scoreError ? (
                    <div className={styles.fieldError} id="player-score-err">
                      {scoreError}
                    </div>
                  ) : scoreDirty ? (
                    <div className={styles.helper}>
                      Was{" "}
                      <code className={styles.code}>{player.score}</code>.
                    </div>
                  ) : (
                    <div className={styles.helper}>
                      Admin override — players can&apos;t set their own score.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ───────── Meta ─────────
              Cosmetics sit above Stats: they are closer to the player's
              identity than the gameplay counters below. */}
          {serverMetaError && (
            <div className={styles.errorAlert}>
              <Alert variant="danger" title="Meta rejected">
                {serverMetaError}
              </Alert>
            </div>
          )}
          <div className={styles.metaCard}>
            <PlayerMetaEditor
              key={metaKey}
              schema={metaSchema}
              value={playerMeta}
              onChange={(next) => {
                setMeta(next);
                setMetaDirty(!jsonObjectEqual(next, playerMeta));
              }}
              onValidityChange={setMetaValid}
            />
          </div>

          {/* ───────── Stats ───────── */}
          {serverStatsError && (
            <div className={styles.errorAlert}>
              <Alert variant="danger" title="Stats rejected">
                {serverStatsError}
              </Alert>
            </div>
          )}
          <div className={styles.statsCard}>
            <PlayerStatsEditor
              key={statsKey}
              schema={statsSchema}
              value={player.stats}
              onChange={(next) => {
                setStats(next);
                setStatsDirty(!jsonObjectEqual(next, player.stats));
              }}
              onValidityChange={setStatsValid}
            />
          </div>
        </div>
      </div>

      {/* ───────── Sticky save bar ───────── */}
      {dirty && (
        <div className={styles.saveBar} role="region" aria-label="Unsaved changes">
          <div className={styles.inner}>
            <div
              className={`${styles.status} ${hasError ? styles.statusErr : ""}`}
            >
              <span className={styles.statusDot} aria-hidden />
              {hasError
                ? `Fix ${errorCount} ${
                    errorCount === 1 ? "error" : "errors"
                  } to save`
                : `${changeCount} unsaved ${
                    changeCount === 1 ? "change" : "changes"
                  }`}
            </div>
            <div className={styles.saveActions}>
              <Button
                variant="ghost"
                onClick={doDiscard}
                disabled={mutation.isPending}
              >
                Discard
              </Button>
              <Button
                variant="primary"
                onClick={doSave}
                disabled={hasError || mutation.isPending}
                loading={mutation.isPending}
                icon={
                  !mutation.isPending ? (
                    <Save size={13} aria-hidden />
                  ) : undefined
                }
              >
                {mutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// === Identity meta summary ========================================== //

interface MetaSummaryProps {
  schema: ProfileConfig["meta_schema"];
  meta: Record<string, unknown>;
}

/**
 * Read-only chip row under the player's name — "what has this player got on"
 * at a glance, without scrolling to the Meta card.
 *
 * Only the string-ish cosmetics become chips: an `int` or `bool` (prestige
 * level, VIP flag) is a counter, not something worn, and reads as noise here.
 * A single-value field constrained by an allow-list is the catalogued cosmetic
 * proper, so it gets the accent treatment.
 */
function MetaSummary({ schema, meta }: MetaSummaryProps) {
  const chips: { key: string; value: string; accent: boolean }[] = [];
  for (const f of schema) {
    const raw = meta[f.key];
    if (f.type === "string") {
      if (typeof raw === "string" && raw !== "") {
        chips.push({
          key: f.key,
          value: raw,
          accent: (f.allowed ?? []).length > 0,
        });
      }
    } else if (f.type === "string[]" && Array.isArray(raw)) {
      raw.forEach((v, i) => {
        if (typeof v === "string" && v !== "") {
          chips.push({ key: `${f.key}-${i}`, value: v, accent: false });
        }
      });
    }
  }
  if (chips.length === 0) return null;

  return (
    <div className={styles.metaSummary}>
      {chips.map((c) => (
        <span
          key={c.key}
          className={`${styles.pillTag} ${c.accent ? styles.pillTagAccent : ""}`}
        >
          {c.value}
        </span>
      ))}
    </div>
  );
}

/**
 * Deep JSON-equality check for a JSONB bag (stats or meta): compares every
 * key's value via JSON.stringify, independent of key order.
 */
function jsonObjectEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i]) return false;
    if (JSON.stringify(a[ak[i]]) !== JSON.stringify(b[bk[i]])) return false;
  }
  return true;
}

// === Loading skeleton =============================================== //
// Identity card + 2 form cards, per the mockup state notes.

function LoadingSkeleton() {
  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.identity}>
            <Skeleton width={52} height={52} radius={999} />
            <div className={styles.skelLines}>
              <Skeleton width={160} height={16} />
              <Skeleton width={120} height={11} />
            </div>
          </div>
          <div className={styles.kvGrid}>
            {[0, 1, 2].map((i) => (
              <div className={styles.kv} key={i}>
                <Skeleton width={70} height={9} />
                <Skeleton width="80%" height={13} style={{ marginTop: 4 }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {[0, 1, 2].map((i) => (
        <div className={styles.card} key={i}>
          <div className={styles.cardHeader}>
            <Skeleton width={90} height={14} />
          </div>
          <div className={styles.cardBody}>
            <Skeleton width={120} height={12} />
            <Skeleton
              width="100%"
              height={36}
              radius={6}
              style={{ marginTop: 6 }}
            />
          </div>
        </div>
      ))}
    </>
  );
}
